/**
 * Raptor Predator Frontend - AI Store
 * 
 * Manages client-side AI model state for WebLLM and ONNX Runtime.
 * All AI inference runs locally in the browser for privacy.
 * 
 * Privacy First: No user prompts or organizational data ever leave the browser.
 * 
 * Peer Reviewed: Gemini 3 Pro (8/10), GPT-5.1 (8/10) - December 7, 2025
 * All TODOs resolved - WebLLM/ONNX service integration implemented
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { AIModelStatus, InferenceRequest, InferenceResult, InferenceType } from '../types';

// Import the actual AI services
import { WebLLMService } from '../services/ai/webllm-service';
import { ONNXService } from '../services/ai/onnx-service';

// Singleton instances for services
let webLLMServiceInstance: WebLLMService | null = null;
let onnxServiceInstance: ONNXService | null = null;

// Service getters (lazy initialization)
function getWebLLMService(): WebLLMService {
  if (!webLLMServiceInstance) {
    webLLMServiceInstance = new WebLLMService();
  }
  return webLLMServiceInstance;
}

function getONNXService(): ONNXService {
  if (!onnxServiceInstance) {
    onnxServiceInstance = new ONNXService();
  }
  return onnxServiceInstance;
}

// ============================================================================
// Types
// ============================================================================

interface ModelInfo {
  id: string;
  name: string;
  description: string;
  sizeBytes: number;
  type: 'llm' | 'embedding' | 'anomaly';
  quantization?: string;
  contextLength?: number;
}

interface InferenceHistory {
  id: string;
  request: InferenceRequest;
  result?: InferenceResult;
  error?: string;
  timestamp: number;
  duration?: number;
}

interface AIConfig {
  preferredModel: string;
  autoLoadOnInit: boolean;
  maxConcurrentInferences: number;
  timeoutMs: number;
}

interface AIState {
  // Initialization state
  initialized: boolean;
  initializing: boolean;
  
  // Configuration
  config: AIConfig;
  
  // Model registry
  availableModels: Map<string, ModelInfo>;
  
  // Model status
  models: Map<string, AIModelStatus>;
  
  // Active model IDs
  activeLlmModel: string | null;
  activeEmbeddingModel: string | null;
  activeAnomalyModel: string | null;
  
  // Inference queue
  inferenceQueue: InferenceRequest[];
  isProcessing: boolean;
  currentInferenceId: string | null;
  
  // Inference history (limited)
  history: InferenceHistory[];
  maxHistoryLength: number;
  
  // Caching
  cachedEmbeddings: Map<string, Float32Array>;
  maxCacheSize: number; // Number of embeddings to cache
  
  // Stats
  totalInferences: number;
  averageLatencyMs: number;
  successRate: number;
  
  // Errors
  lastError: string | null;
}

interface AIActions {
  // Initialization
  initialize: () => Promise<void>;
  loadModel: (modelId: string) => Promise<void>;
  unloadModel: (modelId: string) => Promise<void>;
  setConfig: (config: Partial<AIConfig>) => void;
  
  // Model management
  registerModel: (model: ModelInfo) => void;
  setModelStatus: (modelId: string, status: Partial<AIModelStatus>) => void;
  setActiveModel: (type: 'llm' | 'embedding' | 'anomaly', modelId: string | null) => void;
  
  // Loading progress
  updateLoadingProgress: (modelId: string, progress: number) => void;
  setModelLoaded: (modelId: string, loaded: boolean) => void;
  setModelError: (modelId: string, error: string | null) => void;
  
  // Inference
  queueInference: (request: InferenceRequest) => void;
  startInference: (requestId: string) => void;
  completeInference: (requestId: string, result: InferenceResult) => void;
  failInference: (requestId: string, error: string) => void;
  cancelInference: (requestId: string) => void;
  clearQueue: () => void;
  
  // Embedding cache
  cacheEmbedding: (key: string, embedding: Float32Array) => void;
  getCachedEmbedding: (key: string) => Float32Array | undefined;
  clearEmbeddingCache: () => void;
  
  // History
  clearHistory: () => void;
  
  // Stats
  updateStats: (latencyMs: number, success: boolean) => void;
  
  // Error handling
  setError: (error: string | null) => void;
}

// ============================================================================
// Default Models
// ============================================================================

// PEER REVIEW FIX: Use TinyLlama (1.1B params) instead of Phi-3 (3.8B) for <2GB VRAM
// This addresses the VRAM bottleneck issue raised by Gemini 3 Pro
const defaultModels: ModelInfo[] = [
  {
    id: 'tinyllama-1.1b-q4f16',
    name: 'TinyLlama 1.1B (4-bit)',
    description: 'TinyLlama 1.1B quantized for browser. Optimized for <2GB VRAM. Best for NL→Policy translation.',
    sizeBytes: 0.7 * 1024 * 1024 * 1024, // ~700MB - fits in most GPUs
    type: 'llm',
    quantization: 'q4f16_1',
    contextLength: 2048,
  },
  {
    id: 'phi-3-mini-q4f16',
    name: 'Phi-3 Mini (4-bit)',
    description: 'Microsoft Phi-3 Mini quantized. Requires >2GB VRAM. Better quality but heavier.',
    sizeBytes: 2.4 * 1024 * 1024 * 1024, // ~2.4GB
    type: 'llm',
    quantization: 'q4f16_1',
    contextLength: 4096,
  },
  {
    id: 'bge-small-en-v1.5',
    name: 'BGE Small EN',
    description: 'BAAI BGE embedding model for semantic search. Very lightweight.',
    sizeBytes: 134 * 1024 * 1024, // ~134MB
    type: 'embedding',
  },
  {
    id: 'anomaly-detector-v1',
    name: 'Anomaly Detector v1',
    description: 'Custom ONNX model for telemetry anomaly detection.',
    sizeBytes: 10 * 1024 * 1024, // ~10MB
    type: 'anomaly',
  },
];

// ============================================================================
// Initial State
// ============================================================================

const defaultConfig: AIConfig = {
  preferredModel: 'tinyllama-1.1b-q4f16', // PEER REVIEW: Default to smaller model for broader compatibility
  autoLoadOnInit: false, // Don't auto-load to save bandwidth
  maxConcurrentInferences: 1,
  timeoutMs: 30000,
};

const initialState: AIState = {
  initialized: false,
  initializing: false,
  config: defaultConfig,
  availableModels: new Map(defaultModels.map(m => [m.id, m])),
  models: new Map(),
  activeLlmModel: null,
  activeEmbeddingModel: null,
  activeAnomalyModel: null,
  inferenceQueue: [],
  isProcessing: false,
  currentInferenceId: null,
  history: [],
  maxHistoryLength: 100,
  cachedEmbeddings: new Map(),
  maxCacheSize: 1000,
  totalInferences: 0,
  averageLatencyMs: 0,
  successRate: 1.0,
  lastError: null,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useAIStore = create<AIState & AIActions>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          ...initialState,
          
          // Initialization
          initialize: async () => {
            const state = get();
            if (state.initialized || state.initializing) {
              return;
            }
            
            set((draft) => {
              draft.initializing = true;
            });
            
            try {
              // Register all default models with initial status
              for (const model of defaultModels) {
                set((draft) => {
                  draft.models.set(model.id, {
                    modelId: model.id,
                    modelName: model.name,
                    status: 'idle',
                    loaded: false,
                    loading: false,
                    progress: 0,
                    sizeBytes: model.sizeBytes,
                  });
                });
              }
              
              set((draft) => {
                draft.initialized = true;
                draft.initializing = false;
              });
              
              console.log('[AI Store] Initialized with models:', defaultModels.map(m => m.id));
            } catch (error) {
              set((draft) => {
                draft.initializing = false;
                draft.lastError = error instanceof Error ? error.message : 'Initialization failed';
              });
            }
          },
          
          loadModel: async (modelId: string) => {
            const state = get();
            const modelInfo = state.availableModels.get(modelId);
            
            if (!modelInfo) {
              set((draft) => {
                draft.lastError = `Model ${modelId} not found`;
              });
              return;
            }
            
            const status = state.models.get(modelId);
            if (status?.loaded || status?.loading) {
              return; // Already loaded or loading
            }
            
            set((draft) => {
              const model = draft.models.get(modelId);
              if (model) {
                model.loading = true;
                model.progress = 0;
                delete model.error;
              } else {
                draft.models.set(modelId, {
                  modelId,
                  modelName: modelInfo.name,
                  status: 'downloading',
                  loaded: false,
                  loading: true,
                  progress: 0,
                  sizeBytes: modelInfo.sizeBytes,
                });
              }
            });
            
            try {
              // Actual WebLLM/ONNX model loading via services
              console.log(`[AI Store] Loading model: ${modelId} (type: ${modelInfo.type})`);
              
              if (modelInfo.type === 'llm') {
                // Load LLM model via WebLLM service
                const webllm = getWebLLMService();
                await webllm.initialize({
                  modelId,
                  onProgress: (progress: { progress: number }) => {
                    set((draft) => {
                      const model = draft.models.get(modelId);
                      if (model) {
                        model.progress = Math.round(progress.progress * 100);
                      }
                    });
                  },
                  onReady: () => {
                    console.log(`[AI Store] WebLLM model ready: ${modelId}`);
                  },
                  onError: (error: Error) => {
                    console.error(`[AI Store] WebLLM model error: ${modelId}`, error);
                  },
                });
              } else {
                // Load ONNX model (embedding or anomaly) via ONNX service
                const onnx = getONNXService();
                const modelPath = `/models/${modelId}.onnx`;
                await onnx.loadModel(modelId, {
                  modelPath,
                  backend: 'wasm', // Default to WASM for consistency
                  onProgress: (progress: number) => {
                    set((draft) => {
                      const model = draft.models.get(modelId);
                      if (model) {
                        model.progress = progress;
                      }
                    });
                  },
                  onReady: () => {
                    console.log(`[AI Store] ONNX model ready: ${modelId}`);
                  },
                  onError: (error: Error) => {
                    console.error(`[AI Store] ONNX model error: ${modelId}`, error);
                  },
                });
              }
              
              set((draft) => {
                const model = draft.models.get(modelId);
                if (model) {
                  model.loaded = true;
                  model.loading = false;
                  model.progress = 100;
                  model.cachedAt = new Date();
                }
                
                // Set as active based on type
                if (modelInfo.type === 'llm') {
                  draft.activeLlmModel = modelId;
                } else if (modelInfo.type === 'embedding') {
                  draft.activeEmbeddingModel = modelId;
                } else if (modelInfo.type === 'anomaly') {
                  draft.activeAnomalyModel = modelId;
                }
              });
              
              console.log(`[AI Store] Model loaded: ${modelId}`);
            } catch (error) {
              set((draft) => {
                const model = draft.models.get(modelId);
                if (model) {
                  model.loading = false;
                  model.error = error instanceof Error ? error.message : 'Failed to load model';
                }
                draft.lastError = error instanceof Error ? error.message : 'Failed to load model';
              });
            }
          },
          
          unloadModel: async (modelId: string) => {
            const state = get();
            const status = state.models.get(modelId);
            const modelInfo = state.availableModels.get(modelId);
            
            if (!status?.loaded) {
              return; // Not loaded
            }
            
            // Actual model unloading from memory via services
            console.log(`[AI Store] Unloading model: ${modelId}`);
            
            try {
              if (modelInfo?.type === 'llm') {
                // Unload LLM model from WebLLM
                const webllm = getWebLLMService();
                await webllm.unload();
              } else {
                // Unload ONNX model
                const onnx = getONNXService();
                onnx.unloadModel(modelId);
              }
            } catch (error) {
              console.error(`[AI Store] Error unloading model ${modelId}:`, error);
              // Continue with state cleanup even if unload fails
            }
            
            set((draft) => {
              const model = draft.models.get(modelId);
              if (model) {
                model.loaded = false;
                model.progress = 0;
                delete model.cachedAt;
              }
              
              // Clear active model if this was active
              const info = draft.availableModels.get(modelId);
              if (info?.type === 'llm' && draft.activeLlmModel === modelId) {
                draft.activeLlmModel = null;
              } else if (info?.type === 'embedding' && draft.activeEmbeddingModel === modelId) {
                draft.activeEmbeddingModel = null;
              } else if (info?.type === 'anomaly' && draft.activeAnomalyModel === modelId) {
                draft.activeAnomalyModel = null;
              }
            });
          },
          
          setConfig: (config) => { set((state) => {
            Object.assign(state.config, config);
          }); },
          
          // Model management
          registerModel: (model) => { set((state) => {
            state.availableModels.set(model.id, model);
            
            // Initialize status if not exists
            if (!state.models.has(model.id)) {
              state.models.set(model.id, {
                modelId: model.id,
                modelName: model.name,
                status: 'idle',
                loaded: false,
                loading: false,
                progress: 0,
                sizeBytes: model.sizeBytes,
              });
            }
          }); },
          
          setModelStatus: (modelId, newStatus) => { set((state) => {
            const current = state.models.get(modelId);
            if (current) {
              Object.assign(current, newStatus);
            } else {
              const info = state.availableModels.get(modelId);
              state.models.set(modelId, {
                modelId,
                modelName: info?.name ?? modelId,
                status: newStatus.status ?? 'idle',
                loaded: newStatus.loaded ?? false,
                loading: newStatus.loading ?? false,
                progress: newStatus.progress ?? 0,
                sizeBytes: info?.sizeBytes ?? 0,
                ...newStatus,
              });
            }
          }); },
          
          setActiveModel: (type, modelId) => { set((state) => {
            switch (type) {
              case 'llm':
                state.activeLlmModel = modelId;
                break;
              case 'embedding':
                state.activeEmbeddingModel = modelId;
                break;
              case 'anomaly':
                state.activeAnomalyModel = modelId;
                break;
            }
          }); },
          
          // Loading progress
          updateLoadingProgress: (modelId, progress) => { set((state) => {
            const model = state.models.get(modelId);
            if (model) {
              model.progress = Math.min(100, Math.max(0, progress));
              model.loading = progress < 100;
            }
          }); },
          
          setModelLoaded: (modelId, loaded) => { set((state) => {
            const model = state.models.get(modelId);
            if (model) {
              model.loaded = loaded;
              model.loading = false;
              model.progress = loaded ? 100 : 0;
              if (loaded) {
                model.cachedAt = new Date();
                delete model.error;
              }
            }
          }); },
          
          setModelError: (modelId, error) => { set((state) => {
            const model = state.models.get(modelId);
            if (model) {
              if (error) {
                model.error = error;
              } else {
                delete model.error;
              }
              model.loading = false;
            }
            if (error) {
              state.lastError = error;
            }
          }); },
          
          // Inference
          queueInference: (request) => { set((state) => {
            state.inferenceQueue.push(request);
          }); },
          
          startInference: (requestId) => { set((state) => {
            state.isProcessing = true;
            state.currentInferenceId = requestId;
          }); },
          
          completeInference: (requestId, result) => { set((state) => {
            // Remove from queue
            state.inferenceQueue = state.inferenceQueue.filter(r => r.id !== requestId);
            
            // Find request for history
            const request = state.inferenceQueue.find(r => r.id === requestId);
            
            // Add to history
            state.history.unshift({
              id: requestId,
              request: request ?? { id: requestId, type: 'general-chat' as InferenceType, input: '' },
              result,
              timestamp: Date.now(),
              duration: result.latencyMs,
            });
            
            // Trim history
            if (state.history.length > state.maxHistoryLength) {
              state.history = state.history.slice(0, state.maxHistoryLength);
            }
            
            // Update processing state
            state.isProcessing = state.inferenceQueue.length > 0;
            state.currentInferenceId = state.isProcessing ? state.inferenceQueue[0]?.id ?? null : null;
            
            // Update stats
            state.totalInferences++;
          }); },
          
          failInference: (requestId, error) => { set((state) => {
            // Remove from queue
            const request = state.inferenceQueue.find(r => r.id === requestId);
            state.inferenceQueue = state.inferenceQueue.filter(r => r.id !== requestId);
            
            // Add to history with error
            if (request) {
              state.history.unshift({
                id: requestId,
                request,
                error,
                timestamp: Date.now(),
              });
              
              if (state.history.length > state.maxHistoryLength) {
                state.history = state.history.slice(0, state.maxHistoryLength);
              }
            }
            
            // Update processing state
            state.isProcessing = state.inferenceQueue.length > 0;
            state.currentInferenceId = state.isProcessing ? state.inferenceQueue[0]?.id ?? null : null;
            state.lastError = error;
          }); },
          
          cancelInference: (requestId) => { set((state) => {
            state.inferenceQueue = state.inferenceQueue.filter(r => r.id !== requestId);
            if (state.currentInferenceId === requestId) {
              state.currentInferenceId = state.inferenceQueue[0]?.id ?? null;
              state.isProcessing = state.inferenceQueue.length > 0;
            }
          }); },
          
          clearQueue: () => { set((state) => {
            state.inferenceQueue = [];
            state.isProcessing = false;
            state.currentInferenceId = null;
          }); },
          
          // Embedding cache
          cacheEmbedding: (key, embedding) => { set((state) => {
            // LRU-style: remove oldest if at capacity
            if (state.cachedEmbeddings.size >= state.maxCacheSize) {
              const firstKey = state.cachedEmbeddings.keys().next().value;
              if (firstKey !== undefined) {
                state.cachedEmbeddings.delete(firstKey);
              }
            }
            state.cachedEmbeddings.set(key, embedding);
          }); },
          
          getCachedEmbedding: (key) => {
            return get().cachedEmbeddings.get(key);
          },
          
          clearEmbeddingCache: () => { set((state) => {
            state.cachedEmbeddings.clear();
          }); },
          
          // History
          clearHistory: () => { set((state) => {
            state.history = [];
          }); },
          
          // Stats
          updateStats: (latencyMs, success) => { set((state) => {
            const n = state.totalInferences;
            state.averageLatencyMs = (state.averageLatencyMs * n + latencyMs) / (n + 1);
            
            const successCount = Math.round(state.successRate * n);
            state.successRate = (successCount + (success ? 1 : 0)) / (n + 1);
            
            state.totalInferences = n + 1;
          }); },
          
          // Error handling
          setError: (error) => { set((state) => {
            state.lastError = error;
          }); },
        })),
        {
          name: 'raptor-ai-store',
          partialize: (state) => ({
            // Persist config and model selection, not loaded state
            config: state.config,
            activeLlmModel: state.activeLlmModel,
            activeEmbeddingModel: state.activeEmbeddingModel,
            activeAnomalyModel: state.activeAnomalyModel,
            totalInferences: state.totalInferences,
            averageLatencyMs: state.averageLatencyMs,
            successRate: state.successRate,
          }),
        }
      )
    ),
    { name: 'ai-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectAvailableModels = (state: AIState & AIActions) => state.availableModels;
export const selectModelStatus = (modelId: string) => (state: AIState & AIActions) => state.models.get(modelId);
export const selectActiveLlmModel = (state: AIState & AIActions) => state.activeLlmModel;
export const selectActiveEmbeddingModel = (state: AIState & AIActions) => state.activeEmbeddingModel;
export const selectIsProcessing = (state: AIState & AIActions) => state.isProcessing;
export const selectInferenceQueue = (state: AIState & AIActions) => state.inferenceQueue;
export const selectInferenceHistory = (state: AIState & AIActions) => state.history;
export const selectAIStats = (state: AIState & AIActions) => ({
  totalInferences: state.totalInferences,
  averageLatencyMs: state.averageLatencyMs,
  successRate: state.successRate,
});
export const selectLastError = (state: AIState & AIActions) => state.lastError;

// ============================================================================
// Model Loading Status Selectors
// ============================================================================

export const selectAnyModelLoading = (state: AIState & AIActions): boolean => {
  for (const model of state.models.values()) {
    if (model.loading) {
      return true;
    }
  }
  return false;
};

export const selectLoadedModels = (state: AIState & AIActions): AIModelStatus[] => {
  const result: AIModelStatus[] = [];
  for (const model of state.models.values()) {
    if (model.loaded) {
      result.push(model);
    }
  }
  return result;
};

export const selectTotalModelSize = (state: AIState & AIActions): number => {
  let total = 0;
  for (const model of state.availableModels.values()) {
    total += model.sizeBytes;
  }
  return total;
};

export const selectLoadedModelSize = (state: AIState & AIActions): number => {
  let total = 0;
  for (const status of state.models.values()) {
    if (status.loaded) {
      total += status.sizeBytes;
    }
  }
  return total;
};
